/**
 * Companion "Stop speaking" contract (SDK 2.11.0, 5 Sep 2026).
 *
 * Keith: "when they are speaking add a Stop button so the user can stop
 * them talking, similar to the work we did on the decisionmemory avatar
 * recently. Should be in the avatar SDK; if not, add it — it's a good
 * feature to have across any avatars."
 *
 * The decisionmemory avatar (living-notes' AvatarSurface) stops the avatar
 * through the SDK's `SpeechPlaybackControl`, which dispatches the window
 * event `avatar:live:stopSpeaking`. That event is the ONE contract every
 * surface already understands — useLiveListenControls forwards it to the
 * runtime (`visual.playback.stop`) on call-driven surfaces and
 * useLivePromptPlayback settles browser-played TTS locally — so the
 * Companion's Stop button, `CamlinCompanion.stopSpeaking()` and any other
 * host all speak it rather than inventing a parallel path.
 *
 * Speaking state is published the same way (`camlin-companion:speaking`)
 * so hosts can drive their own affordances via
 * `CamlinCompanion.onSpeakingChange(cb)`.
 */

import type { CompanionSiteConfig } from './types';

/** Window event any host dispatches to cut the avatar off mid-utterance. */
export const COMPANION_STOP_SPEAKING_EVENT = 'avatar:live:stopSpeaking';

/** Window event the widget raises whenever the avatar starts/stops talking. */
export const COMPANION_SPEAKING_EVENT = 'camlin-companion:speaking';

/** Plain-English label — no vendor names (Keith, 5 Sep 2026). */
export const COMPANION_STOP_SPEAKING_LABEL = 'Stop speaking';

export interface CompanionSpeakingChangeDetail {
  speaking: boolean;
}

export type CompanionSpeakingListener = (speaking: boolean) => void;

/** Ask the mounted avatar to stop talking. Safe to call when it is silent. */
export function requestCompanionStopSpeaking(source = 'companion'): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(COMPANION_STOP_SPEAKING_EVENT, { detail: { source } }));
}

/** Publish the avatar's speaking state to hosts (widget-internal). */
export function announceCompanionSpeaking(speaking: boolean): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<CompanionSpeakingChangeDetail>(COMPANION_SPEAKING_EVENT, { detail: { speaking } })
  );
}

/**
 * Subscribe to speaking changes. Returns the unsubscribe function, so the
 * host can hold it exactly like an `addEventListener` cleanup.
 */
export function onCompanionSpeakingChange(listener: CompanionSpeakingListener): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<CompanionSpeakingChangeDetail>).detail;
    listener(!!detail?.speaking);
  };
  window.addEventListener(COMPANION_SPEAKING_EVENT, handler);
  return () => window.removeEventListener(COMPANION_SPEAKING_EVENT, handler);
}

/**
 * `avatar.controls.stop` is on by default — a site owner switches the
 * button off with `{ controls: { stop: false } }`; the programmatic API
 * keeps working regardless.
 */
export function isCompanionStopControlEnabled(
  config: Pick<CompanionSiteConfig, 'avatar'> | null | undefined
): boolean {
  return config?.avatar?.controls?.stop !== false;
}

/**
 * The avatar counts as speaking while the live session reports 'speaking'
 * OR while the in-page speech-playback registry has an active clip. The
 * two overlap in the normal case; either alone is enough to show Stop.
 */
export function deriveCompanionSpeaking(
  liveStatus: string | null | undefined,
  playbackStatus: string | null | undefined
): boolean {
  return liveStatus === 'speaking' || (!!playbackStatus && playbackStatus !== 'idle');
}
